import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { AssistantApi } from "../../shared/api/endpoints";
import type { KDLocation, KDRun } from "../../shared/api/kdTypes";
import { Icon } from "../../shared/ui/Icon";
import { Button, Callout, Select, Spinner } from "../../shared/ui/Primitives";

export function KDSheet({ api, run, location, findingId, onSelect }: {
  api: AssistantApi; run: KDRun; location: KDLocation; findingId: string;
  onSelect: (id: string, location: KDLocation) => void;
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(100);
  const [full, setFull] = useState(false);
  const [pan, setPan] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const picture = useRef<HTMLImageElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const wasFull = useRef(false);
  const expand = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number; moved: boolean } | null>(null);
  const dragged = useRef(false);
  const findings = run.result.findings ?? [];
  const navigable = findings.filter(f => f.locations.length);
  const index = navigable.findIndex(f => f.id === findingId);
  const selected = navigable[index];
  const pages = (run.result.documents ?? []).flatMap(d => d.pages.map(p => ({ document_id: d.id, page: p.page, bbox: [0, 0, 1, 1] as KDLocation["bbox"] })));
  const pageIndex = pages.findIndex(p => p.document_id === location.document_id && p.page === location.page);
  const marks = findings.flatMap((f, i) => f.locations.flatMap((loc, j) =>
    loc.document_id === location.document_id && loc.page === location.page && (loc.bbox[2] - loc.bbox[0] < 1 || loc.bbox[3] - loc.bbox[1] < 1)
      ? [{ finding: f, location: loc, number: i + 1, key: `${f.id}:${j}` }] : []));
  const filename = run.manifest.find(d => d.id === location.document_id)?.filename;

  useEffect(() => {
    let alive = true; let objectUrl = "";
    setUrl(""); setError(""); setLoaded(false);
    void api.downloadKDPage(run.id, location.document_id, location.page).then(file => {
      if (alive) { objectUrl = URL.createObjectURL(file.blob); setUrl(objectUrl); }
    }).catch(e => { if (alive) setError(e instanceof Error ? e.message : "Не удалось загрузить лист"); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [api, run.id, location.document_id, location.page]);
  useEffect(() => {
    if (full) dialog.current?.showModal();
    else if (wasFull.current) expand.current?.focus();
    wasFull.current = full;
  }, [full]);
  useLayoutEffect(() => {
    const el = viewport.current, img = picture.current;
    if (!el || !img || !loaded) return;
    const [x0, y0, x1, y1] = location.bbox;
    const focused = Boolean(findingId) || x0 > 0 || y0 > 0 || x1 < 1 || y1 < 1;
    el.scrollLeft = focused ? (x0 + x1) / 2 * img.clientWidth - el.clientWidth / 2 : 0;
    el.scrollTop = focused ? (y0 + y1) / 2 * img.clientHeight - el.clientHeight / 2 : 0;
  }, [loaded, location, findingId, zoom, full]);

  function jump(offset: number) {
    const f = navigable[(index + offset + navigable.length) % navigable.length];
    if (f?.locations[0]) onSelect(f.id, f.locations[0]);
  }
  function selectPage(i: number) { const p = pages[i]; if (p) onSelect("", p); }
  function fit() {
    const el = viewport.current, img = picture.current;
    if (el && img) setZoom(Math.max(25, Math.min(100, Math.floor(el.clientHeight * img.naturalWidth / img.naturalHeight / el.clientWidth * 100))));
  }
  const content = <div className="kd-sheet">
    <div className="kd-viewer-toolbar">
      <Button variant="secondary" size="small" disabled={pageIndex <= 0} onClick={() => selectPage(pageIndex - 1)}>Предыдущий лист</Button>
      <Select aria-label="Лист документа" value={pageIndex} onChange={e => selectPage(Number(e.target.value))}>
        {pages.map((p, i) => <option key={`${p.document_id}:${p.page}`} value={i}>{run.manifest.find(d => d.id === p.document_id)?.filename} · лист {p.page}</option>)}
      </Select>
      <Button variant="secondary" size="small" disabled={pageIndex < 0 || pageIndex >= pages.length - 1} onClick={() => selectPage(pageIndex + 1)}>Следующий лист</Button>
    </div>
    <div className="kd-viewer-toolbar">
      <Button variant="secondary" size="small" disabled={!navigable.length} onClick={() => jump(index < 0 ? 0 : -1)}>Предыдущее замечание</Button>
      <span aria-live="polite">{selected ? `Замечание ${index + 1} из ${navigable.length}` : `Замечаний с привязкой: ${navigable.length}`}</span>
      <Button variant="secondary" size="small" disabled={!navigable.length} onClick={() => jump(1)}>Следующее замечание</Button>
    </div>
    {selected ? <div className="kd-viewer-description"><strong>{selected.message}</strong>
      {selected.observed && <p>В документе: {selected.observed}</p>}{selected.expected && <p>Для сравнения: {selected.expected}</p>}
      {selected.locations.length > 1 && <div className="kd-page-buttons">{selected.locations.map((loc, i) => <Button variant="secondary" size="small" key={i} onClick={() => onSelect(selected.id, loc)}>Место {i + 1} · лист {loc.page}</Button>)}</div>}
    </div> : null}
    <p className="muted kd-viewer-hint">Перетаскивайте лист мышью или пальцем. Нажмите на рамку, чтобы выбрать замечание. Выбранное выделено красным, остальные — оранжевым.</p>
    <div className="kd-image-tools" role="group" aria-label="Управление чертежом">
      <button type="button" className="icon-button" title="Уменьшить" aria-label="Уменьшить" disabled={zoom <= 25} onClick={() => setZoom(v => Math.max(25, v - 25))}><Icon name="zoomOut" /></button>
      <label className="kd-zoom"><span>{zoom}%</span><input aria-label="Масштаб просмотра" type="range" min="25" max="400" step="5" value={zoom} onChange={e => setZoom(Number(e.target.value))} /></label>
      <button type="button" className="icon-button" title="Увеличить" aria-label="Увеличить" disabled={zoom >= 400} onClick={() => setZoom(v => Math.min(400, v + 25))}><Icon name="zoomIn" /></button>
      <button type="button" className="icon-button" title="Перетаскивание" aria-label="Перетаскивание" aria-pressed={pan} onClick={() => { drag.current = null; setPan(v => !v); }}><Icon name="move" /></button>
      <Button variant="secondary" size="small" onClick={fit}>Лист целиком</Button>
      <Button variant="secondary" size="small" onClick={() => setZoom(100)}>По ширине</Button>
      <button type="button" ref={full ? undefined : expand} className="icon-button kd-image-expand" title={full ? "Закрыть полный экран" : "На весь экран"} aria-label={full ? "Закрыть полный экран" : "На весь экран"} onClick={() => setFull(v => !v)}><Icon name={full ? "close" : "square"} /></button>
    </div>
    {error ? <Callout tone="warning">{error}</Callout> : !url ? <Spinner label="Загружаем лист" /> : <div ref={viewport} className={`kd-sheet-scroll ${pan ? "is-panning" : ""}`} tabIndex={0} aria-label="Поле чертежа"
      onPointerDown={e => {
        if (e.button !== 0 || !pan) return;
        dragged.current = false;
        drag.current = { x: e.clientX, y: e.clientY, left: e.currentTarget.scrollLeft, top: e.currentTarget.scrollTop, moved: false };
      }}
      onPointerMove={e => {
        const start = drag.current; if (!start) return;
        if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 4) {
          start.moved = true; dragged.current = true; e.currentTarget.setPointerCapture(e.pointerId);
        }
        if (start.moved) { e.currentTarget.scrollLeft = start.left - e.clientX + start.x; e.currentTarget.scrollTop = start.top - e.clientY + start.y; }
      }}
      onPointerLeave={() => { if (!drag.current?.moved) drag.current = null; }}
      onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} onLostPointerCapture={() => { drag.current = null; }}
      onClickCapture={e => { if (dragged.current) { e.stopPropagation(); e.preventDefault(); dragged.current = false; } }}>
      <div className="kd-sheet-image" style={{ width: `${zoom}%` }}>
        <img ref={picture} src={url} draggable={false} onLoad={() => setLoaded(true)} alt={`${filename} · лист ${location.page}`} />
        <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-label="Области замечаний">
          {!findingId && (location.bbox[0] > 0 || location.bbox[1] > 0 || location.bbox[2] < 1 || location.bbox[3] < 1) && <rect className="kd-trace-highlight" aria-label="Выбранная запись комплекта" x={location.bbox[0] * 1000} y={location.bbox[1] * 1000} width={(location.bbox[2] - location.bbox[0]) * 1000} height={(location.bbox[3] - location.bbox[1]) * 1000} />}
          {marks.map(mark => { const [x0, y0, x1, y1] = mark.location.bbox;
            return <g key={mark.key} className={mark.finding.id === findingId ? "is-selected" : ""}>
              <rect role="button" tabIndex={0} aria-label={`Замечание ${mark.number}: ${mark.finding.message}`} x={x0 * 1000} y={y0 * 1000} width={(x1 - x0) * 1000} height={(y1 - y0) * 1000}
                onClick={() => onSelect(mark.finding.id, mark.location)} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(mark.finding.id, mark.location); } }}><title>{mark.finding.message}</title></rect>
              <text x={x0 * 1000} y={Math.max(16, y0 * 1000 - 4)}>{mark.number}</text>
            </g>;
          })}
        </svg>
      </div>
    </div>}
  </div>;
  return full ? <dialog ref={dialog} className="kd-sheet-dialog" aria-label="Просмотр чертежа" onCancel={() => setFull(false)} onClose={() => setFull(false)}>{content}</dialog> : content;
}
